import {
  DISPLAY_FONT_FAMILY,
  STORY_FONT_FAMILY,
  STORY_HEIGHT,
  STORY_WIDTH,
} from '../core/canvasDrawing';
import {
  STICKER_CANVAS_HEIGHT,
  STICKER_CANVAS_WIDTH,
  renderSticker,
  renderStory,
} from '../core/renderStory';

const STORY_TYPE = 'image/png';
// Sharp on a phone screen and in the story, where the sticker is drawn about 2.6 times the design's size.
const STICKER_SCALE = 3;
// Enough for the frame picker's small tiles.
const PREVIEW_SCALE = 0.6;

// A canvas draws a font only once the browser has loaded it; without this the first story could use a fallback.
const loadStoryFonts = () => {
  if (!document.fonts) {
    return Promise.resolve();
  }
  return Promise.all([
    ...['normal', 'bold'].map((weight) => document.fonts.load(`${weight} 64px "${STORY_FONT_FAMILY}"`)),
    document.fonts.load(`bold 64px "${DISPLAY_FONT_FAMILY}"`),
  ]);
};

const toBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('The story could not be encoded'))), STORY_TYPE);
});

const drawSticker = (scene, scale) => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(STICKER_CANVAS_WIDTH * scale);
  canvas.height = Math.round(STICKER_CANVAS_HEIGHT * scale);
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);
  renderSticker(ctx, scene);
  return canvas;
};

/**
 * Draws the sticker the client sees and, once there is a photo, the story they share: the same sticker on a full
 * 1080 × 1920 page.
 *
 * @param {Object} scene What renderSticker draws.
 * @param {string} fileName
 * @returns {Promise<{ stickerUrl: string, storyFile: ?File }>} The sticker as an object URL, which the caller
 *   revokes, and the story, or null without a photo.
 */
const composeStory = async (scene, fileName) => {
  await loadStoryFonts();
  const sticker = drawSticker(scene, STICKER_SCALE);
  const stickerUrl = URL.createObjectURL(await toBlob(sticker));
  if (!scene.photo) {
    return { stickerUrl, storyFile: null };
  }
  const canvas = document.createElement('canvas');
  canvas.width = STORY_WIDTH;
  canvas.height = STORY_HEIGHT;
  renderStory(canvas.getContext('2d'), { card: scene.card, sticker });
  return { stickerUrl, storyFile: new File([await toBlob(canvas)], fileName, { type: STORY_TYPE }) };
};

/**
 * Small drawings of the sticker in each frame, for the frame picker.
 *
 * @param {Object} scene What renderSticker draws, without the frame.
 * @param {{ id: string }[]} frames
 * @returns {Promise<Object<string, string>>} A data URL per frame id.
 */
const createFramePreviews = async (scene, frames) => {
  await loadStoryFonts();
  return Object.fromEntries(frames.map((frame) => [
    frame.id,
    drawSticker({ ...scene, frame }, PREVIEW_SCALE).toDataURL(),
  ]));
};

export {
  composeStory,
  createFramePreviews,
};
